import {
  configPrismicReducer,
  defaultFinalCast,
  defaultPrismicReducer,
  initialConfiguration,
  moveToKeys,
} from './reducers';
import { clean } from './utils';
import { BusinessCategory } from 'constants/synergy';
import { PrismicRepositoryType } from 'constants/config';
import { Map, TypedMap } from 'ts/types';
import * as prismic from '@prismicio/client';
import { PrismicDocument } from '@prismicio/types';

const PAGE_SIZE = 100;

const prismicConfig: TypedMap<{
  prismicRepositoryForUrls?: typeof PrismicRepositoryType[keyof typeof PrismicRepositoryType];
  shouldFetchBusinessConfig?: boolean;
  shouldFetchLegacyConfig?: boolean;
  shouldFetchTechnicalConfig?: boolean;
  blackList?: string[];
}> = {
  [BusinessCategory.HOME]: {
    prismicRepositoryForUrls: PrismicRepositoryType.TECHNICAL,
    shouldFetchBusinessConfig: true,
    shouldFetchLegacyConfig: true,
    shouldFetchTechnicalConfig: true,
    blackList: [
      'address_modal_mask_pattern',
      'alert_timeout',
      'book_appointment_days_enabled_to',
      'book_appointment_days_to_show',
      'book_appointment_first_slot_selected',
      'book_appointment_minutes_options',
      'business_category',
      'calculate_distance_engine',
      'confirmation_follow_up_loading_image',
      'contact_phone',
      'contact_reasons',
      'contact_us_image',
      'default_dropout_phone',
      'events_with_provider_details',
      'global_digital_asset',
      'should_have_mobility_continuity',
      'should_have_mobility_continuation_static_page',
      'header_to_be_displayed',
      'icvc_page',
      'identification',
      'is_destination_feature_enabled',
      'journey_type',
      'landing_image',
      'legacy_partner_id',
      'length_unit',
      'long_date_format',
      'map_valid_countries',
      'map_default_location',
      'map_to_render_in_destination_synergy',
      'map_to_render_in_location_synergy',
      'max_icvc_call_attempts',
      'metas',
      'mobility_continuity_first_step',
      'move_version',
      'notification_auto_refresh_time_sec',
      'notifications_modal_interval_time',
      'occupancy_days',
      'partner_name',
      'partner_reference',
      'passenger_adult_options',
      'passenger_child_options',
      'regex',
      'short_date_format',
      'should_display_banner_alert',
      'should_display_book_appointment',
      'should_display_chat_bot_page',
      'should_display_confirmation_button',
      'should_display_confirmation_follow_up_page',
      'should_display_creation_page_button',
      'should_display_decision_tree_icons',
      'should_display_email',
      'should_display_incident_description_info_banner',
      'should_display_incident_location_subtitle',
      'should_display_landing_page_with_plate_number',
      'should_display_legacy_order_number_case_page',
      'should_display_legacy_order_number_history_page',
      'should_display_legacy_order_number_mission_page',
      'should_display_looking_for_provider',
      'should_display_next_steps',
      'should_display_passenger_child_options',
      'should_display_passenger_details',
      'should_display_passenger_details_baby',
      'should_display_passenger_details_disability',
      'should_display_passenger_details_pet',
      'should_display_passenger_details_pregnant',
      'should_display_pictures_continue_modal',
      'should_display_property_occupancy',
      'should_display_repudiation_callback',
      'should_display_request_notifications_popup',
      'should_display_special_alert',
      'should_display_step_header',
      'should_display_step_header_name',
      'should_display_step_header_steps',
      'should_display_steps_progress_bar',
      'should_display_summary_details',
      'should_display_unreachable_call_us_button',
      'should_enable_garage_orientation',
      'should_hide_first_and_last_name_in_motor_contact_details',
      'should_hide_location_details_in_location',
      'should_hide_mobility_continuity_button_on_confirmation_follow_up_page',
      'should_hide_mobility_continuity_button_on_status_page',
      'should_highlight_letter_in_decision_tree',
      'should_provider_details_be_expanded',
      'should_show_alternative_tracking_screen_in_follow_my_case',
      'should_show_confirmation_follow_up_map',
      'should_show_decision_tree_options_not_selected_in_light_grey_color',
      'should_show_live_location_in_follow_my_case',
      'should_show_policy_holder_question',
      'should_show_request_assistance_in_contact_details',
      'should_skip_get_plate',
      'should_show_at_motorway_question_in_location_details',
      'should_show_height_restriction_question_in_location_details',
      'should_show_textarea_not_in_motorway_in_location_details',
      'should_show_contact_us_if_is_in_motorway_in_location_details',
      'step_order',
      'steps_in_follow_my_case',
      'supported_phone_country_codes',
      'tenant_id',
      'time_slot_options',
      'timeout',
      'use_legacy_partner_id',
      'zip_code_pattern',
    ],
  },
  [BusinessCategory.MOTOR]: { shouldFetchLegacyConfig: true },
};

let businessClient: any;
let legacyClient: any;
let technicalClient: any;

const createClient = (
  repositoryType?: typeof PrismicRepositoryType[keyof typeof PrismicRepositoryType],
) => {
  switch (repositoryType) {
    case PrismicRepositoryType.BUSINESS:
      if (!businessClient) {
        businessClient = prismic.createClient(
          process.env.REACT_APP_BUSINESS_PRISMIC_ENDPOINT!,
          { accessToken: process.env.REACT_APP_BUSINESS_PRISMIC_ACCESS_TOKEN },
        );
      }
      return businessClient;
    case PrismicRepositoryType.TECHNICAL:
      if (!technicalClient) {
        technicalClient = prismic.createClient(
          process.env.REACT_APP_TECHNICAL_PRISMIC_ENDPOINT!,
          { accessToken: process.env.REACT_APP_TECHNICAL_PRISMIC_ACCESS_TOKEN },
        );
      }
      return technicalClient;
    default:
      if (!legacyClient) {
        legacyClient = prismic.createClient(
          process.env.REACT_APP_LEGACY_PRISMIC_ENDPOINT!,
          { accessToken: process.env.REACT_APP_LEGACY_PRISMIC_ACCESS_TOKEN },
        );
      }
      return legacyClient;
  }
};

export const fetchDataByTag = async (
  tags: string[],
  reducer = defaultPrismicReducer,
  finalCast = defaultFinalCast,
): Promise<any> => {
  const prismicClient = createClient(
    prismicConfig[
      JSON.parse(JSON.stringify(process.env.REACT_APP_BUSINESS_LINE))
    ]?.prismicRepositoryForUrls,
  );
  const response = await prismicClient.getAllByEveryTag(tags, {
    pageSize: PAGE_SIZE,
  });
  return finalCast(
    response
      .sort(
        (a: PrismicDocument, b: PrismicDocument) =>
          tags.indexOf(b.tags[0]) - tags.indexOf(a.tags[0]),
      )
      .map((config: PrismicDocument) => config?.data)
      .map((data: Map) => clean(data, false))
      .reduce(reducer, {}),
  );
};

const getPrismicConfig = async (
  clientFactory: any,
  tags: string[],
  shouldFetchConfig?: boolean,
  repositoryType?: string,
  environment?: string,
) => {
  let config: any[] = [];
  const configData: Map = {};
  const addConfig = (tag: string) => (_: any, response: any[]) => {
    configData[tag] = !!response[0] && JSON.parse(JSON.stringify(response[0]));
  };
  if (shouldFetchConfig) {
    const client = clientFactory();
    await Promise.all(
      tags.map(async tag => {
        const response = await client.getAllByEveryTag(
          [tag, environment ?? process.env.REACT_APP_environment],
          { pageSize: PAGE_SIZE },
          repositoryType,
        );
        addConfig(tag)(undefined, response);
        return true;
      }),
    );

    const uniqueTags = tags.filter(
      (elem, pos, arr) => arr.indexOf(elem) === pos,
    );

    config = uniqueTags
      .reduce((acc, tag) => [...acc, configData[tag]], config)
      .filter(config => !!config);
  }
  return config;
};

const translateEnvironment = {
  local: 'local',
  po: 'ppa',
  prod: 'pra',
  sta: 'sta',
  test: 'ica',
};

const translateLegacyConfig = (legacyConfig: any[]) => {
  legacyConfig.forEach(element => {
    element.data.custom_steps = element.data.custom_steps.map(
      ({
        primary: {
          category,
          page_name,
          page_type,
          should_display_summary_items_title,
          should_display_title,
          virtual_page_url,
          ...restPrimaryKeys
        },
        ...rest
      }: any) => ({
        primary: {
          category_analytics: category,
          page_name_analytics: page_name,
          page_type_analytics: page_type,
          should_display_summary_input_titles:
            should_display_summary_items_title,
          should_display_title_summary: should_display_title,
          virtual_page_url_analytics: virtual_page_url,
          ...restPrimaryKeys,
        },
        ...rest,
      }),
    );
    element.data.google_optimize_container_id = element.data.optimize_id;
    element.data.google_optimize_experiments_list =
      element.data.optimize_experiments_list.map((experiment: any) => ({
        custom_experiment_name_id: experiment.key,
        experiment_id: experiment.id,
        payload: experiment.payload,
      }));
    element.data.gtm_container_id = element.data.gtm_id;
    element.data.gtm_env = element.data.gtm_env.map(
      (gtmEnv: {
        auth: string;
        environment: keyof typeof translateEnvironment;
        preview: string;
      }) => ({
        auth: gtmEnv.auth,
        environment: translateEnvironment[gtmEnv.environment],
        preview: gtmEnv.preview,
      }),
    );
    element.data.is_qualtrics_enabled = element.data.should_show_surveys;
    element.data.one_trust_env = element.data.one_trust_env.map(
      (oneTrustEnv: {
        env: keyof typeof translateEnvironment;
        id: string;
      }) => ({
        environment: translateEnvironment[oneTrustEnv.env],
        one_trust_id: oneTrustEnv.id,
      }),
    );
    element.data.survey_per_page = element.data.site_intercept_surveys.map(
      (survey: any) => ({
        survey_id: survey.survey_id,
        virtual_page_view: survey.event,
      }),
    );
    element.data.theme_colors = element.data.theme?.find(
      ({ slice_type }: Map) => slice_type === 'theme',
    );
  });
};

const translateBusinessConfig = (businessConfig: any[]) => {
  businessConfig.forEach(element => {
    element.data.theme_colors_business = element.data.theme?.find(
      ({ slice_type }: Map) => slice_type === 'theme',
    );
  });
};

const mergeConfigs = (
  tags: string[],
  configs: any[],
  blackList: string[] = [],
) => {
  const allConfig: any[] = [];
  tags.forEach((tag: string) => {
    configs.forEach((config: any) => {
      if (config.tags.includes(tag)) {
        const confWithTag = allConfig.find((ele: any) =>
          ele.tags.includes(tag),
        );
        if (confWithTag) {
          blackList.forEach(el => {
            delete config.data[el];
          });
          confWithTag.data = { ...confWithTag.data, ...config.data };
        } else {
          allConfig.push(config);
        }
      }
    });
  });
  return allConfig;
};

export const fetchDataByTagAndEnv = async (
  tags: string[],
  shouldFetchBusinessConfig?: boolean,
  shouldFetchLegacyConfig?: boolean,
  shouldFetchTechnicalConfig?: boolean,
  environment?: string,
): Promise<any> => {
  const businessConfigPromise: Promise<any[]> = getPrismicConfig(
    () => createClient(PrismicRepositoryType.BUSINESS),
    tags,
    shouldFetchBusinessConfig ??
      prismicConfig[
        JSON.parse(JSON.stringify(process.env.REACT_APP_BUSINESS_LINE))
      ]?.shouldFetchBusinessConfig,
    PrismicRepositoryType.BUSINESS,
    environment,
  );
  const legacyConfigPromise: Promise<any[]> = getPrismicConfig(
    () => createClient(),
    tags,
    shouldFetchLegacyConfig ??
      prismicConfig[
        JSON.parse(JSON.stringify(process.env.REACT_APP_BUSINESS_LINE))
      ]?.shouldFetchLegacyConfig,
    undefined,
    environment,
  );
  const technicalConfigPromise: Promise<any[]> = getPrismicConfig(
    () => createClient(PrismicRepositoryType.TECHNICAL),
    tags,
    shouldFetchTechnicalConfig ??
      prismicConfig[
        JSON.parse(JSON.stringify(process.env.REACT_APP_BUSINESS_LINE))
      ]?.shouldFetchTechnicalConfig,
    PrismicRepositoryType.TECHNICAL,
    environment,
  );
  const [legacyConfig, businessConfig, technicalConfig] = await Promise.all([
    legacyConfigPromise,
    businessConfigPromise,
    technicalConfigPromise,
  ]);
  translateLegacyConfig(legacyConfig);
  translateBusinessConfig(businessConfig);
  const mergedConfig = mergeConfigs(
    tags,
    [...legacyConfig, ...businessConfig, ...technicalConfig],
    prismicConfig[
      JSON.parse(JSON.stringify(process.env.REACT_APP_BUSINESS_LINE))
    ]?.blackList,
  );
  return moveToKeys(
    mergedConfig
      .sort(
        (a, b) =>
          tags.indexOf(b.tags.find((tag: string) => tags.includes(tag))) -
          tags.indexOf(a.tags.find((tag: string) => tags.includes(tag))),
      )
      .map(config => config?.data)
      .map(data => clean(data, false))
      .reduce(configPrismicReducer, initialConfiguration),
  );
};
